import Component from '@glimmer/component';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import SourceDetailsHeader from 'favoris-web/components/source-details-header';
import MentionsList from '../../components/mentions-list.gts';
import ItemsList from '../../components/items-list.gts';
import type { ContentSource } from 'favoris-web/schemas/content-source';
import type { Mention } from '../../schemas/mention.ts';
import type { Recipe } from '../../schemas/recipe.ts';
import pullToRefresh from '../../modifiers/pull-to-refresh.ts';
import MobileHeader from '../../components/mobile-header.gts';
import MoreActionsMenu from '../../components/more-actions-menu.gts';

interface SourcesShowSignature {
  Args: {
    model: {
      source: ContentSource;
      mentions: Mention[];
      recipes: Recipe[];
      mentionId?: string;
      clearCache?: () => void;
      refresh?: () => unknown;
      onDeleted?: () => void | Promise<void>;
    };
  };
}

export default class SourcesShow extends Component<SourcesShowSignature> {
  @action async refresh() {
    await this.args.model.refresh?.();
  }

  get isUpload(): boolean {
    return this.args.model.source?.sourceType === 'image';
  }

  <template>
    <div {{pullToRefresh this.refresh}}>
      {{#if @model.source}}
        <MobileHeader @scrollOpacity={{true}} @back={{true}}>
          <div class="ml-auto">
            <MoreActionsMenu
              @targetType="content_source"
              @targetId={{@model.source.id}}
              @sourceState={{@model.source.state}}
              @isUpload={{this.isUpload}}
              @onDeleted={{@model.onDeleted}}
              @onReprocessed={{@model.refresh}}
            />
          </div>
        </MobileHeader>

        <div class="mb-4 hidden items-center justify-between lg:flex">
          <LinkTo @route="sources" class="btn btn-ghost btn-sm gap-1">
            &larr; Back to my sources
          </LinkTo>
          <MoreActionsMenu
            @targetType="content_source"
            @targetId={{@model.source.id}}
            @sourceState={{@model.source.state}}
            @isUpload={{this.isUpload}}
            @onDeleted={{@model.onDeleted}}
            @onReprocessed={{@model.refresh}}
          />
        </div>

        <SourceDetailsHeader
          @source={{@model.source}}
          @mentions={{@model.mentions}}
          @mentionId={{@model.mentionId}}
          @clearCache={{@model.clearCache}}
          @refresh={{@model.refresh}}
        />

        {{! Recipes }}
        {{#if @model.recipes.length}}
          <h2 class="mt-8 mb-4 text-2xl font-bold">
            Recipes
            <span class="text-base-content/40 ml-2 text-lg font-normal">
              ({{@model.recipes.length}})
            </span>
          </h2>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {{#each @model.recipes as |recipe|}}
              <LinkTo
                @route="explore.recipe"
                @model={{recipe.id}}
                class="card bg-base-100 no-underline shadow transition-shadow hover:shadow-lg"
              >
                <div class="card-body p-4">
                  <h3 class="card-title text-base">
                    {{#if recipe.categoryEmoji}}
                      <span aria-hidden="true">{{recipe.categoryEmoji}}</span>
                    {{/if}}
                    {{recipe.name}}
                  </h3>
                  <div class="flex flex-wrap gap-1.5">
                    {{#if recipe.cuisine}}
                      <span
                        class="badge badge-sm badge-accent"
                      >{{recipe.cuisine}}</span>
                    {{/if}}
                    {{#if recipe.difficulty}}
                      <span
                        class="badge badge-sm badge-outline"
                      >{{recipe.difficulty}}</span>
                    {{/if}}
                    {{#if recipe.cookTimeMinutes}}
                      <span
                        class="badge badge-sm badge-ghost"
                      >{{recipe.cookTimeMinutes}}
                        min</span>
                    {{/if}}
                  </div>
                  {{#if recipe.summary}}
                    <p class="text-base-content/70 mt-1 line-clamp-2 text-sm">
                      {{recipe.summary}}
                    </p>
                  {{/if}}
                  <div class="text-base-content/50 mt-2 text-xs">
                    {{recipe.ingredientCount}}
                    ingredients &middot;
                    {{recipe.stepCount}}
                    steps
                  </div>
                </div>
              </LinkTo>
            {{/each}}
          </div>
        {{/if}}

        {{! Mentions / Items }}
        {{#if @model.source.creator}}
          <h2 class="mt-8 mb-4 text-2xl font-bold">
            Mentions
            {{#if @model.mentions.length}}
              <span class="text-base-content/40 ml-2 text-lg font-normal">
                ({{@model.mentions.length}})
              </span>
            {{/if}}
          </h2>
          <MentionsList
            @mentions={{@model.mentions}}
            @mentionId={{@model.mentionId}}
            @hideMap={{true}}
          />
        {{else}}
          <h2 class="mt-8 mb-4 text-2xl font-bold">
            Items
            {{#if @model.mentions.length}}
              <span class="text-base-content/40 ml-2 text-lg font-normal">
                ({{@model.mentions.length}})
              </span>
            {{/if}}
          </h2>
          <ItemsList @mentions={{@model.mentions}} />
        {{/if}}
      {{else}}
        <div class="text-base-content/60 py-12 text-center">
          Content source not found.
        </div>
      {{/if}}
    </div>
  </template>
}
